'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { getStrapiMedia } from '../lib/api-helpers';

interface DiscoverMoreCard {
  id: number;
  image: {
    data: {
      attributes: {
        url: string;
        alternativeText?: string;
      };
    };
  };
  discoverMoreNewDescription: string;
  discoverMoreNewReadMoreLink: string;
  cardBackgroundColor?: string;
}

interface DiscoverMoreNewProps {
  titleDiscoverMoreNew: string;
  cards: DiscoverMoreCard[];
  discoverMoreNewSectionBackgroundColor?: string;
}

const DiscoverMoreNew: React.FC<{ data: DiscoverMoreNewProps }> = ({ data }) => {
  const [currentIndex, setCurrentIndex] = useState(0);


  // Add error handling and fallbacks
  if (!data) {
    return <div>No data available</div>;
  }

  const sectionBackgroundColor = data.discoverMoreNewSectionBackgroundColor || '#F0F4FF';
  // Show 1 card on mobile, 2 cards on desktop
  const [cardsPerView, setCardsPerView] = useState(2);
  const cards = data.cards || [];
  const maxIndex = Math.max(0, cards.length - cardsPerView);

  // Handle responsive cards per view
  useEffect(() => {
    const handleResize = () => {
      setCardsPerView(window.innerWidth < 768 ? 1 : 2);
    };
    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset currentIndex if it's out of bounds when cardsPerView changes
  useEffect(() => {
    const newMaxIndex = Math.max(0, cards.length - cardsPerView);
    if (currentIndex > newMaxIndex) {
      setCurrentIndex(0);
    }
  }, [cardsPerView, cards.length, currentIndex]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const visibleCards = cards.slice(currentIndex, currentIndex + cardsPerView);

  return (
    <div 
      className="relative flex items-center justify-center overflow-hidden py-[48px] md:py-[96px] min-h-[660px] md:h-[660px]"
      style={{ 
        backgroundColor: '#A3DDFF2E',
        width: '100%'
      }}
    >
      <div className="container flex flex-col justify-center mx-auto px-[24px] xl:px-0 relative z-20">
        {/* Header with title and navigation */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-8">
          <div className="mb-6 lg:mb-0">
            <h2 
              className="text-[#032d60] font-medium text-xl md:text-2xl"
              style={{ 
                fontWeight: '600',
                fontFamily: 'Inter',
                letterSpacing: '0px',
                whiteSpace: 'nowrap'
              }}
            >
              {data.titleDiscoverMoreNew}
            </h2>
          </div>
          <div className="flex gap-2">
            <button
              onClick={prevSlide}
              className="rounded-full"
              aria-label="Previous cards"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextSlide}
              className="rounded-full"
              aria-label="Next cards"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* Cards container */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {visibleCards.map((card, index) => {
            const imageUrl = getStrapiMedia(card.image.data.attributes.url);
            const cardBgColor = card.cardBackgroundColor || '#e0f2ff';

            return (
              <div
                key={card.id}
                className="bg-white overflow-hidden mx-auto w-full md:w-[525px] h-auto min-h-[350px]"
                style={{ 
                  border: '0.5px solid #AEAEAE', 
                  borderRadius: '10px',
                  maxWidth: '100%'
                }}
              >
                <div className="flex flex-col px-3.5">
                  {/* Image section */}
                  <div 
                    className="overflow-hidden relative w-full h-[180px] md:h-[262px]"
                    style={{ 
                      backgroundColor: cardBgColor,
                      marginTop: '14px',
                      borderRadius: '5px'
                    }}
                  >
                    {imageUrl ? (
                      <Image
                        src={imageUrl}
                        alt={card.image.data.attributes.alternativeText || 'Discover More Card'}
                        width={525}
                        height={262}
                        className="object-cover"
                        style={{ borderRadius: '5px'}}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-6xl mb-2">🦜</div>
                          <div className="text-sm text-gray-500">Featured Image</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content section */}
                  <div className="flex-1 flex flex-col px-3 md:px-4 pb-4">
                    <h3 
                      className="text-black font-medium text-base md:text-lg w-full md:w-[388px] md:h-[48px] h-auto"
                      style={{
                        lineHeight: '24px',
                        fontFamily: 'Inter',
                        fontWeight: '600',
                        letterSpacing: '0px',
                        // marginTop: '16px',
                      }}
                    >
                      {card.discoverMoreNewDescription}
                    </h3>
                    <div className="flex justify-end mt-3 md:mt-4">
                      <Link
                        href={card.discoverMoreNewReadMoreLink}
                        className="flex items-center font-semibold hover:opacity-80 transition-opacity whitespace-nowrap text-base md:text-lg"
                        style={{
                          lineHeight: '15px',
                          fontFamily: 'Inter',
                          letterSpacing: '0px',
                          color: '#032D60',
                          fontWeight: '600',
                          height: '21px'
                        }}
                      >
                        Read More
                        <Image 
                          src="/images/Arrow.svg" 
                          alt=""
                          width={32}
                          height={40}
                          className="ml-1 w-6 h-6 md:w-8 md:h-10 z-[1]"
                        />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DiscoverMoreNew;
